import {
  GATE_LABELS,
  LANG_LABELS,
  PREP_VERDICT_LABELS,
  REVIEW_KIND_LABELS,
  appendToSection,
  CV_HEADINGS,
  type Gate,
  type LetterLang,
} from '@jt/core';
import type { ApplicationFull, Prep, PrepState } from '@jt/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, get, getCsrf, patch, post } from '../api/client';
import { keys } from '../api/hooks';
import { useUI } from '../components/overlays';
import { Icon, Spinner } from '../components/ui';
import { AI_OFF_HINT, readSse, useAi } from '../drafts/useDraft';

/** Открыть «Подготовить отклик»: оценка соответствия, резюме и письмо под вакансию. */
export function useOpenPrep() {
  const ui = useUI();
  const ai = useAi();
  const qc = useQueryClient();
  return (applicationId: string) => {
    if (!ai) return ui.toast(AI_OFF_HINT);
    const a = qc.getQueryData<ApplicationFull>(keys.app(applicationId));
    ui.openSheet({
      title: `Подготовить отклик${a?.company ? ' · ' + a.company : ''}`,
      body: <PrepSheet applicationId={applicationId} />,
    });
  };
}

type Step = 'draft' | 'review' | 'revise';
const STEPS: [Step, string][] = [
  ['draft', 'Черновик резюме и письма'],
  ['review', 'Рецензент проверяет факты и требования'],
  ['revise', 'Правка по замечаниям'],
];

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Нет связи с сервером. Повторите.');
const tokens = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1).replace('.', ',')} тыс.` : String(n));
const usd = (n: number | null) => (n == null ? '' : ` ≈ $${n < 0.01 ? n.toFixed(4) : n.toFixed(2)}`);
const at = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

function GateChip({ g }: { g: Gate }) {
  const cls =
    g === 'pass' ? 'chip-ok' : g === 'fail' ? 'chip-bad' : g === 'flag' ? 'chip-warn' : 'chip-muted';
  return <span className={cls}>{GATE_LABELS[g]}</span>;
}

function List({ title, items, tone }: { title: string; items: string[]; tone?: 'bad' | 'warn' }) {
  if (!items.length) return null;
  return (
    <div className={`prep-list${tone ? ' ' + tone : ''}`}>
      <div className="prep-lh">{title}</div>
      <ul>
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

function Evaluation({ p }: { p: Prep }) {
  const e = p.evaluation!;
  const verdictCls = e.verdict === 'apply' ? 'chip-ok' : e.verdict === 'consider' ? 'chip-warn' : 'chip-bad';
  const dims: [string, number, string][] = [
    ['Навыки', e.scores.skills, e.notes.skills],
    ['Опыт и уровень', e.scores.experience, e.notes.experience],
    ['Направление', e.scores.career, e.notes.career],
  ];
  const gates: [string, Gate, string][] = [
    ['Формат и страна', e.gates.location.verdict, e.gates.location.note],
    ['Зарплата', e.gates.salary.verdict, e.gates.salary.note],
    ['Языки', e.gates.languages.verdict, e.gates.languages.note],
  ];
  const mark = { yes: '✓', partial: '≈', no: '✗' } as const;
  return (
    <section className="sec" aria-label="Оценка соответствия">
      <div className="prep-verdict">
        <span className={verdictCls}>{PREP_VERDICT_LABELS[e.verdict]}</span>
        <span className="prep-score">
          {e.overall}
          <small> из 100</small>
        </span>
        <span className="muted sm">оценено {at(p.evaluatedAt)}</span>
      </div>
      {e.summary && <p className="prep-summary">{e.summary}</p>}
      <List title="Вето — не подходит по условиям" items={e.vetoes} tone="bad" />
      <div className="prep-dims">
        {dims.map(([t, v, n]) => (
          <div key={t}>
            <div className="prep-dim-h">
              <span>{t}</span>
              <b>{v}</b>
            </div>
            <div
              className="meter"
              role="meter"
              aria-valuenow={v}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t}
            >
              <span style={{ width: `${v}%` }} />
            </div>
            {n && <p className="muted sm">{n}</p>}
          </div>
        ))}
      </div>
      <div className="prep-gates">
        {gates.map(([t, g, n]) => (
          <div key={t}>
            <span className="prep-gate-t">{t}</span> <GateChip g={g} />
            {n && <p className="muted sm">{n}</p>}
          </div>
        ))}
      </div>
      <List title="Сильные стороны" items={e.strengths} />
      <List title="Пробелы и риски" items={e.gaps} tone="warn" />
      {e.requirements.length > 0 && (
        <details className="prep-reqs">
          <summary>Требования вакансии · {e.requirements.length}</summary>
          <ul>
            {e.requirements.map((r, i) => (
              <li key={i} className={`m-${r.match}`}>
                <span aria-hidden>{mark[r.match]}</span> {r.text}
                {!r.must && <span className="muted"> (желательно)</span>}
                {r.evidence && <span className="muted"> — {r.evidence}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function PrepSheet({ applicationId }: { applicationId: string }) {
  const ui = useUI();
  const qc = useQueryClient();
  const nav = useNavigate();
  const key = keys.prep(applicationId);
  const q = useQuery({ queryKey: key, queryFn: () => get<PrepState>(`/applications/${applicationId}/prep`) });
  const a = qc.getQueryData<ApplicationFull>(keys.app(applicationId));
  const st = q.data;
  const prep = st?.prep ?? null;

  const [posting, setPosting] = useState<string | null>(null);
  const [busy, setBusy] = useState<'evaluate' | 'documents' | 'save' | 'fetch' | null>(null);
  const [step, setStep] = useState<Step | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const [lang, setLang] = useState<LetterLang | ''>('');
  const [extra, setExtra] = useState('');
  const [tab, setTab] = useState<'cv' | 'letter'>('cv');
  const [cvMd, setCvMd] = useState('');
  const [letter, setLetter] = useState('');
  const version = useRef(0);

  const postingValue = posting ?? prep?.posting ?? st?.postingDraft ?? '';
  const langValue: LetterLang = lang || prep?.lang || 'en';

  // документы с сервера → в поля правки (после генерации и при открытии)
  useEffect(() => {
    if (!prep) return;
    version.current = prep.version;
    setCvMd(prep.cvMd);
    setLetter(prep.letter);
  }, [prep?.documentsAt, prep?.applicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (busy !== 'documents' && busy !== 'evaluate') return;
    const t0 = Date.now();
    setElapsed(0);
    const timer = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);

  const setPrep = (p: Prep) => {
    version.current = p.version;
    qc.setQueryData<PrepState>(key, (old) => ({ postingDraft: '', masterCvReady: true, ...old, prep: p }));
  };

  if (q.isLoading) return <Spinner />;
  if (q.isError || !st) return <p className="muted">Не удалось загрузить. {errText(q.error)}</p>;

  const toSettings = () => {
    ui.closeSheet();
    nav('/settings#master-cv');
  };

  const fetchPosting = async () => {
    setBusy('fetch');
    setError('');
    try {
      const r = await post<{ text: string }>(`/applications/${applicationId}/prep/fetch-posting`);
      setPosting(r.text);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const evaluate = async () => {
    setBusy('evaluate');
    setError('');
    try {
      const p = await post<Prep>(`/applications/${applicationId}/prep/evaluate`, { posting: postingValue });
      setPrep(p);
      setPosting(null);
      setLang('');
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const documents = async () => {
    setBusy('documents');
    setStep('draft');
    setError('');
    try {
      const res = await fetch(`/api/applications/${applicationId}/prep/documents`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-csrf-token': getCsrf() },
        body: JSON.stringify({ lang: langValue, extra }),
        credentials: 'same-origin',
      });
      if (!res.ok) {
        const e = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        setError(e?.error?.message ?? `Ошибка ${res.status}`);
        return;
      }
      let done = false;
      await readSse<{ step?: Step; prep?: Prep; message?: string }>(res, (ev, data) => {
        if (ev === 'step' && data.step) setStep(data.step);
        else if (ev === 'done' && data.prep) {
          done = true;
          setPrep(data.prep);
          setTab('cv');
        } else if (ev === 'error') setError(data.message ?? 'Не получилось. Повторите.');
      });
      if (!done) await q.refetch();
    } catch {
      setError('Связь оборвалась. Сервер доделает документы — откройте окно ещё раз через минуту.');
    } finally {
      setBusy(null);
      setStep(null);
    }
  };

  /** Правки резюме и письма — на сервер, перед PDF и при уходе с поля. */
  const flush = async (next?: { cvMd: string }) => {
    if (!prep) return true;
    const body: { cvMd?: string; letter?: string } = {};
    const md = next?.cvMd ?? cvMd;
    if (md !== prep.cvMd) body.cvMd = md;
    if (letter !== prep.letter) body.letter = letter;
    if (!Object.keys(body).length) return true;
    try {
      setPrep(
        await patch<Prep>(`/applications/${applicationId}/prep`, { ...body, version: version.current }),
      );
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        ui.toast('Документы изменили в другом окне — показываю актуальную версию');
        const fresh = await q.refetch();
        if (fresh.data?.prep) {
          setCvMd(fresh.data.prep.cvMd);
          setLetter(fresh.data.prep.letter);
        }
      } else ui.toast(errText(e));
      return false;
    }
  };

  /** Строку смежного опыта — в раздел навыков резюме. */
  const addAdjacent = (text: string) => {
    const md = appendToSection(cvMd, CV_HEADINGS[prep?.lang ?? 'en'].skills, text);
    setCvMd(md);
    setTab('cv');
    void flush({ cvMd: md }).then(
      (ok) => ok && ui.toast('Добавлено в раздел навыков — поправьте, если нужно'),
    );
  };

  const openPdf = async (doc: 'cv' | 'letter') => {
    const w = window.open('', '_blank');
    if (!(await flush())) return w?.close();
    const url = `/api/applications/${applicationId}/prep/pdf/${doc}`;
    if (w) w.location.href = url;
    else window.location.href = url;
  };

  const save = async () => {
    if (!(await flush())) return;
    setBusy('save');
    try {
      setPrep(await post<Prep>(`/applications/${applicationId}/prep/save`));
      await qc.invalidateQueries({ queryKey: keys.app(applicationId) });
      await qc.invalidateQueries({ queryKey: ['files'] });
      ui.toast('PDF сохранены во вложения вакансии');
    } catch (e) {
      ui.toast(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const ev = prep?.evaluation ?? null;
  const rv = prep?.review ?? null;
  const usage = prep?.usage;
  const hasDocs = !!prep?.documentsAt;
  let usageLine: ReactNode = null;
  if (usage && usage.totalTokens) {
    const parts = [
      usage.evaluate && `оценка ${tokens(usage.evaluate.inputTokens + usage.evaluate.outputTokens)}`,
      usage.documents && `документы ${tokens(usage.documents.inputTokens + usage.documents.outputTokens)}`,
    ].filter(Boolean);
    usageLine = (
      <p className="muted sm prep-usage">
        Расход Claude по этой вакансии: {parts.join(', ')} токенов{usd(usage.costUsd)} (все запуски).
      </p>
    );
  }

  return (
    <>
      {!st.masterCvReady && (
        <section className="sec prep-need">
          <h3>Нужно мастер-резюме</h3>
          <p className="muted">
            Claude берёт факты о вас только из него: опыт, цифры, навыки, языки. Вставьте полное резюме
            текстом или заполните из файла PDF/DOCX — один раз, дальше оно используется для всех вакансий.
          </p>
          <button className="btn primary" onClick={toSettings}>
            Заполнить мастер-резюме
          </button>
        </section>
      )}

      <section className="sec">
        <h3>1. Вакансия</h3>
        <label className="f">
          Полный текст вакансии — требования, обязанности, условия
          <textarea
            rows={8}
            value={postingValue}
            onChange={(e) => setPosting(e.target.value)}
            placeholder="Вставьте текст вакансии со страницы, из LinkedIn или письма"
          />
        </label>
        {prep && prep.postingFlags.length > 0 && posting == null && (
          <div className="prep-list warn">
            <div className="prep-lh">
              В тексте вакансии есть строки, похожие на команды для ИИ — они не выполняются:
            </div>
            <ul>
              {prep.postingFlags.map((f, i) => (
                <li key={i}>«{f}»</li>
              ))}
            </ul>
          </div>
        )}
        <div className="btnrow" style={{ marginTop: 10 }}>
          <button
            className="btn primary"
            onClick={evaluate}
            disabled={!!busy || !st.masterCvReady || postingValue.trim().length < 80}
          >
            <Icon name="sparkles" size={16} /> {ev ? 'Оценить заново' : 'Оценить соответствие'}
          </button>
          {a?.url && (
            <button className="btn ghost" onClick={fetchPosting} disabled={!!busy}>
              Взять текст со страницы вакансии
            </button>
          )}
        </div>
        {busy === 'evaluate' && (
          <p className="muted sm" role="status">
            Claude сверяет вакансию с вашим профилем… {elapsed} с
          </p>
        )}
        {busy === 'fetch' && (
          <p className="muted sm" role="status">
            Открываю страницу…
          </p>
        )}
      </section>

      {error && (
        <p className="prep-error" role="alert">
          {error}
        </p>
      )}

      {ev && prep && <Evaluation p={prep} />}

      {ev && (
        <section className="sec">
          <h3>2. Резюме и сопроводительное письмо</h3>
          {ev.verdict === 'skip' && (
            <p className="prep-list bad">
              Claude не советует подаваться. Документы всё равно можно подготовить.
            </p>
          )}
          <div className="grid">
            <label className="f">
              Язык документов
              <select value={langValue} onChange={(e) => setLang(e.target.value as LetterLang)}>
                {Object.entries(LANG_LABELS).map(([k, t]) => (
                  <option key={k} value={k}>
                    {t}
                    {k === ev.lang ? ' — язык вакансии' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="f">
              Что учесть (необязательно)
              <input
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                placeholder="Например: акцент на API-тестирование, выход с 1 декабря"
              />
            </label>
          </div>
          <div className="btnrow" style={{ marginTop: 10 }}>
            <button className="btn primary" onClick={documents} disabled={!!busy}>
              <Icon name="sparkles" size={16} />{' '}
              {hasDocs ? 'Переделать резюме и письмо' : 'Сделать резюме и письмо'}
            </button>
          </div>
          {busy === 'documents' && (
            <ol className="prep-steps" role="status" aria-live="polite">
              {STEPS.map(([k, t]) => {
                const i = STEPS.findIndex((s) => s[0] === k);
                const cur = STEPS.findIndex((s) => s[0] === step);
                return (
                  <li key={k} className={i < cur ? 'done' : i === cur ? 'now' : ''}>
                    {t}
                    {i === cur ? ` · ${elapsed} с` : ''}
                  </li>
                );
              })}
              <li className="muted">Обычно 1–3 минуты. Окно можно закрыть — результат сохранится.</li>
            </ol>
          )}
          {!busy && !hasDocs && (
            <p className="muted sm">
              Факты — только из мастер-резюме: чего нет в профиле, Claude не впишет, а покажет как пробел.
            </p>
          )}
        </section>
      )}

      {hasDocs && prep && (
        <section className="sec">
          <div className="seg" role="tablist" style={{ marginBottom: 10 }}>
            <button
              role="tab"
              aria-pressed={tab === 'cv'}
              aria-selected={tab === 'cv'}
              onClick={() => setTab('cv')}
            >
              Резюме
            </button>
            <button
              role="tab"
              aria-pressed={tab === 'letter'}
              aria-selected={tab === 'letter'}
              onClick={() => setTab('letter')}
            >
              Письмо
            </button>
          </div>
          {tab === 'cv' ? (
            <label className="f">
              Резюме (можно править: # имя, ## раздел, ### место работы, - пункт, **жирный**)
              <textarea
                className="out prep-doc"
                rows={18}
                value={cvMd}
                onChange={(e) => setCvMd(e.target.value)}
                onBlur={() => void flush()}
              />
            </label>
          ) : (
            <label className="f">
              Сопроводительное письмо (можно править; абзацы — через пустую строку)
              <textarea
                className="out prep-doc"
                rows={18}
                value={letter}
                onChange={(e) => setLetter(e.target.value)}
                onBlur={() => void flush()}
              />
            </label>
          )}
          <div className="btnrow" style={{ marginTop: 10 }}>
            <button className="btn sm" onClick={() => void openPdf(tab)} disabled={!!busy}>
              <Icon name="file" size={14} /> Открыть PDF
            </button>
            <button className="btn sm primary" onClick={save} disabled={!!busy}>
              {busy === 'save'
                ? 'Сохраняю…'
                : prep.savedAt
                  ? 'Сохранить заново во вложения'
                  : 'Сохранить PDF во вложения'}
            </button>
          </div>
          {prep.savedAt && prep.cvFileId && prep.letterFileId && (
            <p className="muted sm">
              Сохранено {at(prep.savedAt)}: <a href={`/api/files/${prep.cvFileId}/download`}>резюме</a> ·{' '}
              <a href={`/api/files/${prep.letterFileId}/download`}>письмо</a> — во вкладке «Файлы» вакансии.
            </p>
          )}
          <p className="pnote">Ничего не отправляется — откликаетесь вы сами.</p>
        </section>
      )}

      {hasDocs && rv && (
        <section className="sec" aria-label="Проверка">
          <h3>Проверка</h3>
          <List
            title="Проверка фактов: этого нет в мастер-резюме — уберите или поправьте"
            items={rv.warnings}
            tone="bad"
          />
          {rv.warnings.length === 0 && (
            <p className="ok-text sm">Цифры, компании и источники пунктов найдены в мастер-резюме.</p>
          )}
          <List
            title="Не вошло из мастер-резюме — достижения с цифрами; верните, если важны"
            items={rv.dropped}
            tone="warn"
          />
          {rv.adjacent.length > 0 && (
            <div className="prep-list">
              <div className="prep-lh">Похожий опыт под требования вакансии</div>
              <ul className="prep-adj">
                {rv.adjacent.map((x, i) => {
                  const inCv = x.used || cvMd.includes(x.text) || letter.includes(x.text);
                  return (
                    <li key={i}>
                      <span className="muted">{x.requirement} ← </span>
                      {x.text}
                      {x.similar && <span className="muted"> ({x.similar})</span>}{' '}
                      {inCv ? (
                        <span className="chip-muted">в документах</span>
                      ) : (
                        <button className="linkbtn" onClick={() => addAdjacent(x.text)} disabled={!!busy}>
                          Добавить в резюме
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <List title="Не вписано — нет ни прямого, ни похожего опыта" items={rv.notClaimed} tone="warn" />
          {rv.questions.length > 0 && (
            <div className="prep-list">
              <div className="prep-lh">
                Вопросы к вам — если это было, допишите в мастер-резюме и переделайте документы
              </div>
              <ul>
                {rv.questions.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
              <button className="linkbtn" onClick={toSettings}>
                Открыть мастер-резюме
              </button>
            </div>
          )}
          {rv.keywords.length > 0 && (
            <p className="sm">
              <span className="muted">Ключевые слова вакансии в документах: </span>
              {rv.keywords.join(', ')}
            </p>
          )}
          {(rv.issues.length > 0 || rv.summary) && (
            <details className="prep-reqs">
              <summary>Рецензент о черновике (учтено в этой версии) · {rv.issues.length}</summary>
              {rv.summary && <p className="sm">{rv.summary}</p>}
              <ul>
                {rv.issues.map((i, n) => (
                  <li key={n}>
                    <b>{REVIEW_KIND_LABELS[i.kind]}</b> ({i.doc === 'cv' ? 'резюме' : 'письмо'}): {i.problem}
                    {i.quote && <span className="muted"> — «{i.quote}»</span>}
                    {i.fix && <span className="muted"> → {i.fix}</span>}
                  </li>
                ))}
              </ul>
              {rv.missing.length > 0 && (
                <p className="sm muted">Не затронуто в черновике: {rv.missing.join('; ')}</p>
              )}
            </details>
          )}
        </section>
      )}

      {usageLine}
    </>
  );
}
